import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "react-oidc-context";
import { AiOutlineArrowLeft } from "react-icons/ai";
import { Sidebar } from "../components/Sidebar";
import { PageHeader, LoadingState, ErrorState } from "../components/UI";

function RoomNumberManager() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingRooms, setSavingRooms] = useState({});
  const [saveMessages, setSaveMessages] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFloor, setSelectedFloor] = useState("all");
  const [editingRooms, setEditingRooms] = useState({});

  const auth = useAuth();
  const token = auth.user.access_token;
  // const API_URL = process.env.REACT_APP_API_URL;
  const API_URL = "/api";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Récupération des chambres et des infos de l'hôtel en parallèle
        const [roomsResponse, hotelResponse] = await Promise.all([
          axios.get(`${API_URL}/hotels/${id}/rooms`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/hotels/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const sortedRooms = roomsResponse.data.result.sort((a, b) => a.room_nb.localeCompare(b.room_nb, undefined, { numeric: true }));
        setRooms(sortedRooms);
        setHotel(hotelResponse.data.result);
      } catch (err) {
        // Gestion d'erreur pour le chargement des données
        setError("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, API_URL, token]);

  // Pour initialiser le champ d'édition sans affecter l'affichage
  useEffect(() => {
    if (rooms.length > 0) {
      // Initialiser editingRooms avec les valeurs actuelles
      const initialEditingState = {};
      rooms.forEach((room) => {
        initialEditingState[room.id] = room.room_nb;
      });
      setEditingRooms(initialEditingState);
    }
  }, [rooms]);

  const handleRoomNumberChange = (roomId, newNumber) => {
    // Empêcher la saisie d'une chaîne vide
    if (newNumber.trim() === "") {
      // On peut quand même mettre à jour le champ pour permettre à l'utilisateur
      // d'effacer et recommencer la saisie, mais il ne pourra pas sauvegarder
      setEditingRooms((prev) => ({ ...prev, [roomId]: newNumber }));

      // Optionnel: afficher un message d'avertissement
      setSaveMessages((prev) => ({
        ...prev,
        [roomId]: "Le numéro ne peut pas être vide",
      }));
    } else {
      // Effacer le message d'erreur si présent
      if (saveMessages[roomId] && saveMessages[roomId].includes("vide")) {
        setSaveMessages((prev) => {
          const newMessages = { ...prev };
          delete newMessages[roomId];
          return newMessages;
        });
      }

      setEditingRooms((prev) => ({ ...prev, [roomId]: newNumber }));
    }
  };

  const handleSaveRoom = async (roomId) => {
    setSavingRooms((prev) => ({ ...prev, [roomId]: true }));
    try {
      const newNumber = editingRooms[roomId];
      const currentRoom = rooms.find((room) => room.id === roomId);

      // Vérifier si le numéro est vide
      if (!newNumber || newNumber.trim() === "") {
        setSaveMessages((prev) => ({
          ...prev,
          [roomId]: "Le numéro de chambre ne peut pas être vide",
        }));
        setTimeout(() => {
          setSaveMessages((prev) => {
            const newMessages = { ...prev };
            delete newMessages[roomId];
            return newMessages;
          });
        }, 3000);

        // Restaurer l'ancien numéro
        setEditingRooms((prev) => ({
          ...prev,
          [roomId]: currentRoom.room_nb,
        }));

        setSavingRooms((prev) => ({ ...prev, [roomId]: false }));
        return;
      }

      // Vérifier si le numéro a réellement changé
      if (newNumber === currentRoom.room_nb) {
        setSaveMessages((prev) => ({
          ...prev,
          [roomId]: "Aucun changement détecté",
        }));
        setTimeout(() => {
          setSaveMessages((prev) => {
            const newMessages = { ...prev };
            delete newMessages[roomId];
            return newMessages;
          });
        }, 3000);
        setSavingRooms((prev) => ({ ...prev, [roomId]: false }));
        return;
      }

      await axios.patch(`${API_URL}/hotels/${id}/rooms/${roomId}`, { room_nb: newNumber }, { headers: { Authorization: `Bearer ${token}` } });

      // Mettre à jour la liste des chambres avec le nouveau numéro
      setRooms((prevRooms) => {
        const updatedRooms = prevRooms.map((room) => (room.id === roomId ? { ...room, room_nb: newNumber } : room));
        // Re-trier les chambres après la mise à jour
        return updatedRooms.sort((a, b) => a.room_nb.localeCompare(b.room_nb, undefined, { numeric: true }));
      });

      setSaveMessages((prev) => ({
        ...prev,
        [roomId]: "Modification sauvegardée avec succès !",
      }));
      setTimeout(() => {
        setSaveMessages((prev) => {
          const newMessages = { ...prev };
          delete newMessages[roomId];
          return newMessages;
        });
      }, 3000);

      // Mettre à jour l'état d'édition avec la nouvelle valeur
      setEditingRooms((prev) => ({
        ...prev,
        [roomId]: newNumber,
      }));
    } catch (error) {
      setSaveMessages((prev) => ({
        ...prev,
        [roomId]: "Erreur lors de la sauvegarde",
      }));
      // Gestion d'erreur pour la sauvegarde
    } finally {
      setSavingRooms((prev) => ({ ...prev, [roomId]: false }));
    }
  };

  const filteredRooms = rooms.filter((room) => {
    const matchesSearch = room.room_nb.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFloor = selectedFloor === "all" || room.floor_id === parseInt(selectedFloor);
    return matchesSearch && matchesFloor;
  });

  const uniqueFloors = [...new Set(rooms.map((room) => room.floor_id))].sort((a, b) => a - b);

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return <LoadingState message="Chargement des données..." />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-6 overflow-auto">
        <PageHeader title={`Gestion des numéros de chambres - ${hotel?.name}`} onGoBack={handleGoBack} />

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher une chambre</label>
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#365644] focus:border-transparent" placeholder="Tapez le numéro de chambre..." />
          </div>

          {filteredRooms.length === 0 ? (
            <div className="text-center py-8 text-gray-500">{searchTerm ? "Aucune chambre trouvée avec ce numéro" : "Aucune chambre disponible"}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Numéro actuel</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nouveau numéro</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRooms.map((room) => (
                    <tr key={room.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{room.room_nb}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input type="text" value={editingRooms[room.id] !== undefined ? editingRooms[room.id] : ""} onChange={(e) => handleRoomNumberChange(room.id, e.target.value)} className="border border-gray-300 rounded-md px-3 py-2 w-40 focus:outline-none focus:ring-2 focus:ring-[#365644] focus:border-transparent" placeholder="Nouveau numéro" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap relative">
                        <button onClick={() => handleSaveRoom(room.id)} disabled={savingRooms[room.id] || !editingRooms[room.id] || editingRooms[room.id].trim() === ""} className={`px-4 py-2 rounded-md text-white text-sm font-medium transition-colors duration-200 ${savingRooms[room.id] || !editingRooms[room.id] || editingRooms[room.id].trim() === "" ? "bg-gray-400 cursor-not-allowed" : "bg-[#365644] hover:bg-[#2d4c3c]"}`}>
                          {savingRooms[room.id] ? "Enregistrement..." : "Enregistrer"}
                        </button>
                        {saveMessages[room.id] && <div className={`absolute left-0 top-full mt-1 text-sm font-medium ${saveMessages[room.id].includes("succès") ? "text-green-600" : "text-red-600"}`}>{saveMessages[room.id]}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RoomNumberManager;
